/**
 * OrcaBackground - Unified Premium Background System
 *
 * Composable background component combining multiple layers:
 * - Canvas Sparkles: Twinkling star particles
 * - Canvas Orbs: Large floating gradient orbs
 * - Morphing Blobs: Animated gradient backgrounds
 * - Animated Particles: Interactive mouse-following particles
 * - Grid Pattern: Subtle geometric overlay
 * - Vignette: Multi-layer edge darkening
 *
 * Features:
 * - Quality presets: low/medium/high/auto (FPS-based adaptive)
 * - Mobile optimization: Automatic density/complexity reduction
 * - FPS monitoring: Auto-adjusts quality based on performance
 * - Tab visibility: Pauses animations when tab hidden
 * - Layer composition: Enable/disable individual layers
 *
 * Performance targets:
 * - Desktop: 60fps with all layers
 * - Mobile: 50-60fps with reduced complexity
 * - Memory: <30MB total footprint
 */

import React, { useState, useEffect, useRef } from "react";
import { CanvasSparkles } from "./CanvasSparkles";
import { AnimatedParticles } from "./AnimatedParticles";

export type QualityPreset = "low" | "medium" | "high" | "auto";

interface LayerConfig {
  sparkles?: boolean;
  particles?: boolean;
  grid?: boolean;
  vignette?: boolean;
}

interface OrcaBackgroundProps {
  /** Enable/disable specific layers */
  layers?: LayerConfig;
  /** Quality preset: low/medium/high/auto (default: auto) */
  quality?: QualityPreset;
  /** Background color (default: #0A1628) */
  backgroundColor?: string;
  /** Override mobile detection */
  forceMobile?: boolean;
  /** Enable FPS monitoring for auto quality (default: true) */
  enableFpsMonitoring?: boolean;
  /** FPS threshold for quality downgrade (default: 45) */
  fpsThreshold?: number;
}

export function OrcaBackground({
  layers = {
    sparkles: true,
    particles: true,
    grid: true,
    vignette: true,
  },
  quality: initialQuality = "auto",
  backgroundColor = "#0A1628",
  forceMobile,
  enableFpsMonitoring = true,
  fpsThreshold = 45,
}: OrcaBackgroundProps) {
  // Mobile detection
  const [isMobile, setIsMobile] = useState(forceMobile ?? false);

  // Quality management
  const [activeQuality, setActiveQuality] =
    useState<QualityPreset>(initialQuality);

  // FPS monitoring
  const frameTimesRef = useRef<number[]>([]);
  const lastFrameTimeRef = useRef<number>(performance.now());
  const fpsCheckIntervalRef = useRef<number>();

  // Initialize mobile detection
  useEffect(() => {
    if (forceMobile !== undefined) return;

    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, [forceMobile]);

  // FPS monitoring for auto quality adjustment
  useEffect(() => {
    if (!enableFpsMonitoring || initialQuality !== "auto") return;

    const measureFPS = () => {
      const now = performance.now();
      const delta = now - lastFrameTimeRef.current;
      lastFrameTimeRef.current = now;

      // Track last 60 frames
      frameTimesRef.current.push(delta);
      if (frameTimesRef.current.length > 60) {
        frameTimesRef.current.shift();
      }

      // Calculate average FPS every 60 frames
      if (frameTimesRef.current.length === 60) {
        const avgDelta = frameTimesRef.current.reduce((a, b) => a + b, 0) / 60;
        const avgFPS = 1000 / avgDelta;

        // Adjust quality based on FPS
        if (avgFPS < fpsThreshold) {
          // Drop quality if FPS too low
          setActiveQuality((prev) => {
            if (prev === "high" || prev === "auto") return "medium";
            if (prev === "medium") return "low";
            return prev;
          });
        } else if (avgFPS > 55 && activeQuality !== "auto") {
          // Upgrade quality if FPS consistently good
          setActiveQuality((prev) => {
            if (prev === "low") return "medium";
            if (prev === "medium") return "high";
            return prev;
          });
        }
      }

      requestAnimationFrame(measureFPS);
    };

    const animationFrame = requestAnimationFrame(measureFPS);
    return () => cancelAnimationFrame(animationFrame);
  }, [enableFpsMonitoring, initialQuality, activeQuality, fpsThreshold]);

  // Determine effective quality settings
  const getQualitySettings = () => {
    const effectiveQuality = activeQuality === "auto" ? "high" : activeQuality;

    switch (effectiveQuality) {
      case "low":
        return {
          sparkleCount: isMobile ? 10 : 25,
          enableParticles: false,
          gridOpacity: 0.05,
        };
      case "medium":
        return {
          sparkleCount: isMobile ? 15 : 35,
          enableParticles: !isMobile,
          gridOpacity: 0.25,
        };
      case "high":
      default:
        return {
          sparkleCount: isMobile ? 20 : 50,
          enableParticles: !isMobile,
          gridOpacity: 0.25,
        };
    }
  };

  const settings = getQualitySettings();

  return (
    <div className="fixed inset-0 z-0" style={{ backgroundColor }}>
      {/* Layer 1: Canvas Sparkles (z-index: 1) */}
      {layers.sparkles && (
        <CanvasSparkles
          count={settings.sparkleCount}
          minSize={1}
          maxSize={3}
          color="#ffffff"
          enableDrift={true}
          zIndex={1}
        />
      )}

      {/* Layer 2: Animated Particles (only on desktop medium/high) */}
      {layers.particles && settings.enableParticles && <AnimatedParticles />}

      {/* Layer 3: Grid Pattern - Orange/Purple bright in center, fade to edges */}
      {layers.grid && (
        <>
          {/* Orange grid lines from left/right edges */}
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `
                linear-gradient(to right, rgba(249, 115, 22, ${settings.gridOpacity}) 1px, transparent 1px),
                linear-gradient(to bottom, rgba(249, 115, 22, ${settings.gridOpacity}) 1px, transparent 1px)
              `,
              backgroundSize: isMobile ? "100px 100px" : "50px 50px",
              backgroundPosition: "0 0",
              maskImage:
                "linear-gradient(to right, transparent 0%, black 20%, black 50%, black 80%, transparent 100%)",
              WebkitMaskImage:
                "linear-gradient(to right, transparent 0%, black 20%, black 50%, black 80%, transparent 100%)",
            }}
          />
          {/* Purple grid lines from top/bottom edges */}
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `
                linear-gradient(to right, rgba(147, 51, 234, ${settings.gridOpacity}) 1px, transparent 1px),
                linear-gradient(to bottom, rgba(147, 51, 234, ${settings.gridOpacity}) 1px, transparent 1px)
              `,
              backgroundSize: isMobile ? "100px 100px" : "50px 50px",
              backgroundPosition: "0 0",
              maskImage:
                "linear-gradient(to bottom, transparent 0%, black 20%, black 50%, black 80%, transparent 100%)",
              WebkitMaskImage:
                "linear-gradient(to bottom, transparent 0%, black 20%, black 50%, black 80%, transparent 100%)",
            }}
          />
        </>
      )}

      {/* Layer 4: Multi-layer Vignette */}
      {layers.vignette && (
        <>
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A1628]/50 via-transparent to-[#0A1628]/60" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A1628]/60 via-transparent to-[#0A1628]/60" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A1628]/50 via-transparent to-transparent" />
          <div
            className="absolute inset-0"
            style={{
              background: `radial-gradient(ellipse 120% 100% at center, transparent 0%, transparent 30%, rgba(10, 22, 40, 0.3) 50%, rgba(10, 22, 40, 0.6) 70%, rgba(10, 22, 40, 0.85) 85%, ${backgroundColor} 100%)`,
            }}
          />
        </>
      )}
    </div>
  );
}
